import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { getBlogPostBySlug } from '../services/api';
import BlogQuestionBox from '../components/BlogQuestionBox';
import './Blog.css';

function sanitizeHtmlFragment(inputHtml) {
  const parser = new DOMParser();
  const documentRef = parser.parseFromString(inputHtml || '', 'text/html');

  const allowedTags = new Set([
    'A',
    'B',
    'BLOCKQUOTE',
    'BR',
    'EM',
    'H2',
    'H3',
    'HR',
    'I',
    'IMG',
    'LI',
    'OL',
    'P',
    'STRONG',
    'TABLE',
    'TBODY',
    'TD',
    'TH',
    'THEAD',
    'TR',
    'U',
    'UL'
  ]);

  const allowedAttrs = {
    A: new Set(['href', 'title', 'target', 'rel']),
    IMG: new Set(['src', 'alt', 'title'])
  };

  const nodes = Array.from(documentRef.body.querySelectorAll('*'));

  nodes.forEach((node) => {
    if (!allowedTags.has(node.tagName)) {
      const textNode = documentRef.createTextNode(node.textContent || '');
      node.replaceWith(textNode);
      return;
    }

    Array.from(node.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value || '';
      const isAllowed = allowedAttrs[node.tagName]?.has(attribute.name) || false;

      if (!isAllowed) {
        node.removeAttribute(attribute.name);
        return;
      }

      if (node.tagName === 'A' && name === 'href') {
        const safeHref = value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/');
        if (!safeHref) {
          node.removeAttribute(attribute.name);
        }
      }

      if (node.tagName === 'IMG' && name === 'src') {
        const safeSrc = value.startsWith('http://') || value.startsWith('https://');
        if (!safeSrc) {
          node.removeAttribute(attribute.name);
        }
      }
    });

    if (node.tagName === 'A') {
      node.setAttribute('rel', 'noopener noreferrer');
      if (node.getAttribute('href')?.startsWith('http')) {
        node.setAttribute('target', '_blank');
      }
    }
  });

  return documentRef.body.innerHTML;
}

function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNotFound, setIsNotFound] = useState(false);

  useEffect(() => {
    const loadPost = async () => {
      try {
        const data = await getBlogPostBySlug(slug);
        setPost(data);
      } catch (error) {
        if (error?.response?.status === 404) {
          setIsNotFound(true);
        } else {
          console.error('Failed to load blog post:', error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  // Per-post title, description and canonical (index.html defaults to the homepage).
  useEffect(() => {
    if (!post) return;
    const url = `https://rkubeservices.com/blog/${slug}`;
    document.title = `${post.title} | RKube Services`;
    const setAttr = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    setAttr('link[rel="canonical"]', 'href', url);
    setAttr('meta[name="description"]', 'content', post.excerpt || post.title);
    setAttr('meta[property="og:url"]', 'content', url);
    setAttr('meta[property="og:title"]', 'content', post.title);
    setAttr('meta[property="og:description"]', 'content', post.excerpt || post.title);
  }, [post, slug]);

  const sanitizedBodyHtml = useMemo(() => {
    if (!post?.body_html) {
      return '';
    }
    return sanitizeHtmlFragment(post.body_html);
  }, [post]);

  if (isNotFound) {
    return <Navigate to="/blog" replace />;
  }

  if (isLoading) {
    return (
      <div className="rkblog-page">
        <div className="rkblog-content">
          <div className="container rkblog-container">
            <div className="rkblog-empty-state">
              <h2 className="rkblog-empty-title">Loading article...</h2>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="rkblog-page">
        <div className="rkblog-content">
          <div className="container rkblog-container">
            <div className="rkblog-empty-state">
              <h2 className="rkblog-empty-title">Unable to load article</h2>
              <p className="rkblog-empty-message">Please try again later.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rkblog-page">
      <div className="rkblog-hero">
        <div className="container rkblog-container rkblog-post-hero">
          <p className="rkblog-card-meta">
            {new Date(post.publishedAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
          <h1 className="rkblog-title rkblog-post-title">{post.title}</h1>
          <p className="rkblog-subtitle">{post.excerpt}</p>
        </div>
      </div>

      <div className="rkblog-content">
        <div className="container rkblog-container">
          <Link to="/blog" className="rkblog-back-link rkblog-back-link-top">
            Back to all articles
          </Link>
          <article className="rkblog-post-container">
            <div className="rkblog-post-content" dangerouslySetInnerHTML={{ __html: sanitizedBodyHtml }} />
          </article>
          <BlogQuestionBox slug={slug} title={post.title} />
          <Link to="/blog" className="rkblog-back-link">
            Back to all articles
          </Link>
        </div>
      </div>
    </div>
  );
}

export default BlogPost;
